/* eslint-disable max-classes-per-file */
import { Type } from "@angular/core";
import Handsontable from "handsontable";
import { dropdownRenderer } from "handsontable/renderers";
import _ from "lodash";
import { HotDoc, IHotDocSettings } from "./doc";
import { IHotCellAngularRenderedCellComponent } from "./hot-cell-angular-renderer.service";

export enum Cellstyle {

    Default = 0,

    Readonly = 1,

    Bold = 2,

    ActivePeriod = 4,

    Uppercase = 8,

    NoRightBorder = 16,

    NoBottomBorder = 32,

    White = 64,

    AlignLeft = 128,

    StrongTop = 256,

    Flash = 512,

    VerticalAlignMiddle = 1024,

    AlignRight = 2048,

    StrongBottom = 4096,

    AlignCenter = 8192,

    Red = 16384,

    Blue = 32768,

    Yellow = 65536,

    ReadOnlyInvisibleText = 131072,

    Italic = 262144,

    Green = 524288
}

export enum Celltype {
    text = "text",
    numeric = "numeric",
    dropdown = "dropdown",
}

/* eslint-disable no-bitwise */
export class CellFormatter {
// eslint-disable-next-line sonarjs/public-static-readonly
    static applyStyle = (flags: any, td: HTMLTableCellElement, cellProperties: any) => {
        if (flags & Cellstyle.Readonly) {
            cellProperties.readOnly = true;
            td.classList.add("hot-readonly");
        }

        if (flags & Cellstyle.ActivePeriod) {
            td.classList.add("hot-active");
        }

        if (flags & Cellstyle.Flash) {
            td.classList.add("hot-flash");
        }

        CellFormatter.applyTextStyle(flags, td, cellProperties);
        CellFormatter.applyBorder(flags, td, cellProperties);
        CellFormatter.applyAlign(flags, td, cellProperties);
        CellFormatter.applyColor(flags, td, cellProperties);
    };

    private static readonly applyTextStyle = (flags: any, td: HTMLTableCellElement, cellProperties: any) => {
        if (flags & Cellstyle.ReadOnlyInvisibleText) {
            cellProperties.readOnly = true;
            td.classList.add("hot-readonly-invisible-text");
        }
        if (flags & Cellstyle.Bold) {
            td.classList.add("hot-bold");
        }
        if (flags & Cellstyle.Italic) {
            td.classList.add("hot-italic");
        }
        if (flags & Cellstyle.Uppercase) {
            td.classList.add("hot-uppercase");
        }
        if (flags & Cellstyle.StrongTop) {
            td.classList.add("hot-strong-top");
        }
    };

    private static readonly applyBorder = (flags: any, td: HTMLTableCellElement, cellProperties: any) => {
        if (flags & Cellstyle.NoRightBorder) {
            td.classList.add("hot-hide-right-border");
        }

        if (flags & Cellstyle.NoBottomBorder) {
            td.classList.add("hot-hide-bottom-border");
        }

        if (flags & Cellstyle.StrongBottom) {
            td.classList.add("hot-strong-bottom");
        }
    };

    private static readonly applyAlign = (flags: any, td: HTMLTableCellElement, cellProperties: any) => {
        if (flags & Cellstyle.AlignLeft) {
            td.classList.add("hot-align-left");
        }

        if (flags & Cellstyle.AlignCenter) {
            td.classList.add("hot-align-center");
        }

        if (flags & Cellstyle.AlignRight) {
            td.classList.add("hot-align-right");
        }

        if (flags & Cellstyle.VerticalAlignMiddle) {
            td.classList.add("hot-valign-middle");
        }
    };

    private static readonly applyColor = (flags: any, td: HTMLTableCellElement, cellProperties: any) => {
        if (flags & Cellstyle.White) {
            td.classList.add("hot-white");
        }

        if (flags & Cellstyle.Red) {
            td.classList.add("hot-red");
        }

        if (flags & Cellstyle.Yellow) {
            td.classList.add("hot-yellow");
        }

        if (flags & Cellstyle.Blue) {
            td.classList.add("hot-blue");
        }

        if (flags & Cellstyle.Green) {
            td.classList.add("hot-green");
        }
    };
}

export interface IHotCellSettings {
    colSpan?: number;
    rowSpan?: number;
    zeroReplace?: string;
    noZero?: boolean;
}

export class CellChangeArg {

    readonly rawNewValue: any;
    constructor(raw: any, public newValue: any, protected readonly onComplete: () => void) {
        this.rawNewValue = raw;
    }

    complete() {
        this.onComplete();
    }
}

export class NumericCellChangeArg extends CellChangeArg {
    constructor(readonly raw: any, readonly onComplete: () => void, numericParser?: (value: any) => number) {
        super(raw, raw ? numericParser(raw) : 0, onComplete);
    }
}

export abstract class HotCell {
// eslint-disable-next-line sonarjs/public-static-readonly
    static hotNumber = "hot-number";
    // eslint-disable-next-line sonarjs/public-static-readonly
    static hotDirty = "hot-dirty";

    constructor(protected flags?: Cellstyle, public settings?: IHotCellSettings, public onChange?: (change: CellChangeArg) => void) { }

    renderer = (hot: Handsontable, td: HTMLTableCellElement, row: any, col: any, prop: any, value: any, cellProperties: any): HTMLTableCellElement => {

        this.applyStyle(td, cellProperties);
        td.innerHTML = this.genHtml();
        if ((hot.getSettings() as IHotDocSettings).readOnly) {
            cellProperties.readOnly = true;
        }
        return td;
    };

    abstract validate(value: any): boolean;

    abstract onDelete(settings: IHotDocSettings): void;

    get type(): string { return Celltype.text; }

    changeArgumentFactory(raw: any, onComplete: () => any, settings?: IHotDocSettings): CellChangeArg {
        return new NumericCellChangeArg(raw, onComplete, settings?.raskSettings?.numericParser);
    }

    setReadOnly(): void {
        /* eslint-disable no-bitwise */
        this.flags |= Cellstyle.Readonly;
    }

    get isReadonly(): boolean {
        return !!(this.flags && (this.flags & Cellstyle.Readonly) === Cellstyle.Readonly);
    }

    protected applyStyle(td: HTMLTableCellElement, cellProperties: any) {
        CellFormatter.applyStyle(this.flags, td, cellProperties);
    }
    protected abstract genHtml(): string;
}

export class HotAngularCell<T extends IHotCellAngularRenderedCellComponent> extends HotCell {

    protected ngComponent: Type<T>;
    protected ngComponentDataInit: (instance: T) => void;

    constructor(
        protected flags?: Cellstyle,
        public settings?: IHotCellSettings,
        public onChange?: (change: NumericCellChangeArg) => void) {
        super(flags, settings, onChange);
    }

    static create<T extends IHotCellAngularRenderedCellComponent>(
        ngComponent: Type<T>,
        componentDataInit?: (instance: T) => void,
        flags?: Cellstyle,
        settings?: IHotCellSettings,
        onChange?: (change: NumericCellChangeArg) => void) {
        const cell = new HotAngularCell(flags, settings, onChange);
        cell.ngComponent = ngComponent;
        cell.ngComponentDataInit = componentDataInit;
        return cell;
    }

    renderer = (hot: Handsontable, td: HTMLTableCellElement, row: any, col: any, prop: any, value: any, cellProperties: any): HTMLTableCellElement => {
        this.applyStyle(td, cellProperties);
        const hotSettings = (hot.getSettings() as IHotDocSettings);
        if (hotSettings.readOnly) {
            cellProperties.readOnly = true;
        }
        return hotSettings.raskInfra.hotCellAngularRendererService.renderAngularComponent(
            hot,
            td,
            row,
            col,
            prop,
            value,
            cellProperties,
            this.ngComponent,
            this.ngComponentDataInit);
    };

    validate(value: any): boolean {
        return false;
    }

    override onDelete(): void { }

    protected applyStyle(td: HTMLTableCellElement, cellProperties: any) {
        CellFormatter.applyStyle(this.flags, td, cellProperties);
    }
    protected genHtml() {
        return "";
    }
}

export class HotCellNumeric extends HotCell {
    value: number;
    roundFn: (val: number) => number;

    constructor(rawvalue: number, flags?: Cellstyle, onChange?: (newValue) => void, roundFn?: (val: number) => number) {
        super(flags, null, onChange);
        this.roundFn = roundFn;
        this.value = this.internalRound(rawvalue);
    }

    validate(value: any): boolean {
        return _.isNumber(value) || !isNaN(value);
    }

    override onDelete(): void { }

    override get type(): string {
        return Celltype.numeric;
    }

    protected genHtml(): string {
        return this.format(this.value);
    }

    protected applyStyle(td: HTMLTableCellElement, cellProperties: any) {
        td.classList.add(HotCell.hotNumber);

        if (this.value < 0) {
            td.classList.add("hot-alert");
        }

        super.applyStyle(td, cellProperties);
    }

    private format(value: number): string {
        const val = _.isNumber(value) ? this.internalRound(value) : 0;
        return val !== 0 ? val.toLocaleString("en").replace(/,/g, " ") : "";
    }

    private internalRound(rawvalue: number): number {
        if (this.roundFn) {
            return this.roundFn(rawvalue);
        } else {
            return Math.round(rawvalue);
        }
    }
}

export class HotCellText extends HotCell {
    constructor(public value: string, flags?: Cellstyle, settings?: any) {
        super(flags, settings);
    }

    validate(value: any): boolean {
        return true;
    }

    override onDelete(): void { }

    override get type(): string {
        return Celltype.text;
    }

    protected genHtml(): string {
        return this.value || "";
    }
}

export class HotCellDropdown extends HotCell {
    constructor(public value: string, flags?: Cellstyle, settings?: any, onChange?: (args: CellChangeArg) => void) {
        super(flags, settings, onChange);
    }

    validate(_value: any): boolean {
        return true;
    }

    renderer = (hot: Handsontable, td: HTMLTableCellElement, row: any, col: any, prop: any, value: any, cellProperties: any): HTMLTableCellElement => {
        this.applyStyle(td, cellProperties);
        if ((hot.getSettings() as IHotDocSettings).readOnly) {
            cellProperties.readOnly = true;
        }
        dropdownRenderer(hot, td, row, col, prop, value, cellProperties);
        return td;
    };

    override onDelete(): void { }

    override get type(): string {
        return Celltype.dropdown;
    }

    protected genHtml(): string {
        return this.value || "";
    }

    protected override applyStyle(td: HTMLTableCellElement, cellProperties: any): void {
        if (this?.isReadonly === true) {
            cellProperties.readOnly = true;
            cellProperties.className = "hot-readonly";
            if (this?.type === Celltype.dropdown) {
                cellProperties.editor = false;
            }
        }

        super.applyStyle(td, cellProperties);
    }
}

export abstract class ClickableCell extends HotCell {
    stopImmediatePropagationActive = true;
    constructor(flags?: Cellstyle, settings?: any) {
        super(flags, settings);
    }

    validate(value: any): boolean {
        return false;
    }

    override onDelete(): void { }

    abstract onClick(hotDoc: HotDoc): void;
}
